/**
 * `editor-plugin-show-diff`'s default (`'standard'`) colour scheme already shares its
 * background/border tokens with the streaming highlight for the default Rovo agent, but once the
 * multi-agent telepointer redesign is live, streaming can also render a hashed per-agent hue or
 * the neutral grey fallback (see `resolveTelepointerPresentation`). This resolves the matching
 * `editor-plugin-show-diff` colour scheme for the same agent, so Review moment's diff can be
 * painted with `showDiff({ colorScheme, ... })` and match what the reader just watched stream in.
 */
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ColorScheme } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';

import type { AgentInfo } from '../../pm-plugins/decoration/reducer';
import { resolveTelepointerPresentation } from '../components/TelepointerAvatar/telepointerPresentation';

/**
 * `editor-shared-styles`'s `participantColors` palette, reduced to the accent hue name
 * `editor-plugin-show-diff` accepts. Indices 0-9 are the base palette (one entry per hue);
 * indices 10-17 are a second, lighter tier of the same eight non-grayscale hues (no second tier
 * for gray or lime) — folded back onto their base hue name here, since show-diff's attribution
 * schemes only define one tone per hue. Keep in sync with `participantColors` in
 * `@atlaskit/editor-shared-styles`'s `consts.ts`.
 */
const PARTICIPANT_COLOR_SCHEME_NAMES: readonly ColorScheme[] = [
	'red',
	'blue',
	'green',
	'yellow',
	'purple',
	'magenta',
	'teal',
	'orange',
	'lime',
	'gray',
	'blue',
	'red',
	'orange',
	'yellow',
	'green',
	'teal',
	'purple',
	'magenta',
];

const isReviewMomentAgentDiffColorEnabled = (): boolean =>
	isExperimentEnabled('platform_editor_agent_mentions') &&
	isExperimentEnabled('platform_editor_agent_telepointer_redesign');

/**
 * Resolves the `editor-plugin-show-diff` colour scheme a Review moment diff should use to match
 * the streaming highlight of the agent that produced it.
 *
 * Returns `undefined` when the diff should keep show-diff's own default (`'standard'`, purple):
 * that already matches the streaming highlight tokens for the default Rovo agent, and it's also
 * what streaming itself falls back to while the telepointer redesign experiment is off.
 */
export const resolveReviewMomentDiffColorScheme = (
	agentInfo: AgentInfo | undefined,
): ColorScheme | undefined => {
	if (!isReviewMomentAgentDiffColorEnabled()) {
		return undefined;
	}

	const presentation = resolveTelepointerPresentation(agentInfo);

	switch (presentation.telepointerAgentKind) {
		case 'rovo':
			return undefined;
		case 'agent-third-party':
		case 'fallback':
			// Matches `NEUTRAL_GRAY` in `telepointerPresentation.ts` (gray.subtlest background,
			// gray border) — the `'gray'` attribution scheme resolves to the identical tokens.
			return 'gray';
		case 'agent':
			return presentation.participantColorIndex !== undefined
				? PARTICIPANT_COLOR_SCHEME_NAMES[presentation.participantColorIndex]
				: undefined;
		default:
			return undefined;
	}
};
